import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Mail, Download, Trash2, Loader2, AlertCircle, Globe,
  LayoutGrid, List, Search, ArrowUpDown, ArrowUp, ArrowDown,
  ChevronDown,
} from "lucide-react";
import { API, LEAD_STATUSES } from "@/lib/admin-constants";
import LeadDetailSheet from "./LeadDetailSheet";

const SOURCE_COLORS = {
  commonwealth_fc: "bg-[#0C2E3C] text-white",
  cville_united: "bg-blue-700 text-white",
  cvilleunited: "bg-blue-700 text-white",
};

const SOURCE_LABELS = {
  commonwealth_fc: "CFC",
  cville_united: "CUFC",
  cvilleunited: "CUFC",
};

const STATUS_DOT = {};
LEAD_STATUSES.forEach((s) => { STATUS_DOT[s.id] = s.color; });

const SourceBadge = ({ source }) => {
  if (!source || source === "commonwealth_fc") return null;
  const colorClass = SOURCE_COLORS[source] || "bg-gray-500 text-white";
  const label = SOURCE_LABELS[source] || source.toUpperCase();
  return (
    <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[9px] font-bold rounded ${colorClass}`}>
      <Globe size={8} />
      {label}
    </span>
  );
};

const StatusBadge = ({ statusId }) => {
  const status = LEAD_STATUSES.find((s) => s.id === statusId) || { label: statusId, color: "bg-gray-400" };
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-[10px] font-bold rounded-full text-white ${status.color}`}>
      {status.label}
    </span>
  );
};

const formatDate = (d) => {
  if (!d) return "—";
  return new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

const CRMPipeline = () => {
  const [leads, setLeads] = useState([]);
  const [selectedLead, setSelectedLead] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [leadTypeFilter, setLeadTypeFilter] = useState("all");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [selectedLeads, setSelectedLeads] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [draggedLead, setDraggedLead] = useState(null);
  const [unreadLeads, setUnreadLeads] = useState({});
  const [viewMode, setViewMode] = useState("table");
  const [searchQuery, setSearchQuery] = useState("");
  const [sortCol, setSortCol] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const [statusFilter, setStatusFilter] = useState("all");
  const [teamFilter, setTeamFilter] = useState("all");
  const [seasonFilter, setSeasonFilter] = useState("all");

  const fetchLeads = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/leads`, { withCredentials: true });
      setLeads(res.data);
    } catch (error) {
      console.error("Failed to fetch leads:", error);
    }
  }, []);

  const fetchUnreadLeads = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/email/unread-count`, { withCredentials: true });
      setUnreadLeads(res.data.unread_leads || {});
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    fetchLeads();
    fetchUnreadLeads();
    const interval = setInterval(fetchUnreadLeads, 20000);
    return () => clearInterval(interval);
  }, [fetchLeads, fetchUnreadLeads]);

  const handleLeadClick = (lead) => { setSelectedLead(lead); setSheetOpen(true); };
  const handleSheetClose = () => { setSheetOpen(false); setSelectedLead(null); fetchUnreadLeads(); };
  const handleUpdate = () => {
    fetchLeads().then(() => {
      if (selectedLead) {
        const updated = leads.find((l) => l.lead_id === selectedLead.lead_id);
        if (updated) setSelectedLead(updated);
      }
    });
  };
  const handleLeadDeleted = (leadId) => {
    setLeads((prev) => prev.filter((l) => l.lead_id !== leadId));
    setSelectedLeads((prev) => { const next = new Set(prev); next.delete(leadId); return next; });
  };

  const filteredLeads = useMemo(() => {
    const q = searchQuery.toLowerCase();
    return leads.filter((l) =>
      (leadTypeFilter === "all" || (l.lead_type || "player") === leadTypeFilter) &&
      (sourceFilter === "all" || (l.source || "commonwealth_fc") === sourceFilter) &&
      (statusFilter === "all" || l.status === statusFilter) &&
      (teamFilter === "all" || (l.teamsnap_team_name || "") === teamFilter) &&
      (seasonFilter === "all" || (l.teamsnap_season || "") === seasonFilter) &&
      (!q || l.name?.toLowerCase().includes(q) || l.email?.toLowerCase().includes(q) || l.phone?.includes(q) || l.player_age?.toLowerCase().includes(q) || l.teamsnap_team_name?.toLowerCase().includes(q))
    );
  }, [leads, leadTypeFilter, sourceFilter, statusFilter, teamFilter, seasonFilter, searchQuery]);

  // Derive unique team names and seasons for filters
  const teamNames = useMemo(() => [...new Set(leads.map((l) => l.teamsnap_team_name).filter(Boolean))].sort(), [leads]);
  const seasonNames = useMemo(() => [...new Set(leads.map((l) => l.teamsnap_season).filter(Boolean))].sort(), [leads]);

  const sortedLeads = useMemo(() => {
    const sorted = [...filteredLeads].sort((a, b) => {
      let va = a[sortCol] || "";
      let vb = b[sortCol] || "";
      if (sortCol === "created_at") { va = new Date(va || 0); vb = new Date(vb || 0); }
      else { va = String(va).toLowerCase(); vb = String(vb).toLowerCase(); }
      if (va < vb) return sortDir === "asc" ? -1 : 1;
      if (va > vb) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [filteredLeads, sortCol, sortDir]);

  const leadsForStatus = useCallback((statusId) => filteredLeads.filter((l) => l.status === statusId), [filteredLeads]);

  const handleStatusChange = async (leadId, newStatus) => {
    try {
      await axios.put(`${API}/leads/${leadId}/status`, { status: newStatus }, { withCredentials: true });
      fetchLeads();
      toast.success("Lead moved");
    } catch { toast.error("Failed to move lead"); }
  };

  const toggleLeadSelect = (e, leadId) => {
    e.stopPropagation();
    setSelectedLeads((prev) => { const next = new Set(prev); if (next.has(leadId)) next.delete(leadId); else next.add(leadId); return next; });
  };

  const handleBulkDelete = async () => {
    if (selectedLeads.size === 0) return;
    setBulkDeleting(true);
    try {
      await axios.post(`${API}/leads/bulk-delete`, { lead_ids: [...selectedLeads] }, { withCredentials: true });
      toast.success(`${selectedLeads.size} lead(s) deleted`);
      setSelectedLeads(new Set());
      fetchLeads();
    } catch { toast.error("Failed to delete leads"); }
    setBulkDeleting(false);
  };

  const handleDragStart = (e, lead) => { setDraggedLead(lead); e.dataTransfer.effectAllowed = "move"; e.target.style.opacity = "0.5"; };
  const handleDragEnd = (e) => { e.target.style.opacity = "1"; setDraggedLead(null); };
  const handleDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; };
  const handleDrop = async (e, newStatus) => { e.preventDefault(); if (draggedLead && draggedLead.status !== newStatus) await handleStatusChange(draggedLead.lead_id, newStatus); };

  const exportCSV = async () => {
    try {
      const response = await axios.get(`${API}/leads/export/csv`, { withCredentials: true, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `leads_export_${new Date().toISOString().split("T")[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("CSV exported");
    } catch { toast.error("Failed to export CSV"); }
  };

  const toggleSort = (col) => {
    if (sortCol === col) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortCol(col); setSortDir("asc"); }
  };

  const renderSortIcon = (col) => {
    if (sortCol !== col) return <ArrowUpDown size={12} className="opacity-30" />;
    return sortDir === "asc" ? <ArrowUp size={12} /> : <ArrowDown size={12} />;
  };

  return (
    <div data-testid="crm-pipeline">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading text-3xl font-bold text-cfc-primary">CRM Pipeline</h1>
        <div className="flex items-center gap-2">
          {/* View toggle */}
          <div className="flex items-center border border-cfc-border rounded-sm overflow-hidden" data-testid="view-toggle">
            <button onClick={() => setViewMode("table")} className={`p-2 ${viewMode === "table" ? "bg-cfc-primary text-white" : "text-cfc-text-muted hover:text-cfc-primary"}`} data-testid="view-table-btn"><List size={16} /></button>
            <button onClick={() => setViewMode("kanban")} className={`p-2 ${viewMode === "kanban" ? "bg-cfc-primary text-white" : "text-cfc-text-muted hover:text-cfc-primary"}`} data-testid="view-kanban-btn"><LayoutGrid size={16} /></button>
          </div>
          <Button onClick={exportCSV} variant="outline" className="border-cfc-primary text-cfc-primary hover:bg-cfc-primary hover:text-white" data-testid="export-csv-btn">
            <Download size={16} className="mr-1.5" /> CSV
          </Button>
        </div>
      </div>

      {/* Filters row */}
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-cfc-text-muted" />
          <Input placeholder="Search name, email, phone..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-9 h-9 text-sm" data-testid="lead-search" />
        </div>
        <div className="flex items-center bg-cfc-cream-alt rounded-sm overflow-hidden border border-cfc-border" data-testid="lead-type-filter">
          {[{ id: "all", label: "All" }, { id: "player", label: "Players" }, { id: "coach", label: "Coaches" }].map((f) => (
            <button key={f.id} onClick={() => setLeadTypeFilter(f.id)} className={`px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider ${leadTypeFilter === f.id ? "bg-cfc-primary text-white" : "text-cfc-text-muted hover:text-cfc-primary"}`} data-testid={`filter-${f.id}`}>{f.label}</button>
          ))}
        </div>
        {/* Status filter for table view */}
        {viewMode === "table" && (
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="h-9 px-3 text-xs font-bold border border-cfc-border rounded-sm bg-white text-cfc-primary" data-testid="status-filter">
            <option value="all">All Statuses</option>
            {LEAD_STATUSES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
        )}
        {/* Team filter */}
        {teamNames.length > 0 && (
          <select value={teamFilter} onChange={(e) => setTeamFilter(e.target.value)} className="h-9 px-3 text-xs font-bold border border-cfc-border rounded-sm bg-white text-cfc-primary" data-testid="team-filter">
            <option value="all">All Teams</option>
            {teamNames.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        )}
        {/* Season filter */}
        {seasonNames.length > 0 && (
          <select value={seasonFilter} onChange={(e) => setSeasonFilter(e.target.value)} className="h-9 px-3 text-xs font-bold border border-cfc-border rounded-sm bg-white text-cfc-primary" data-testid="season-filter">
            <option value="all">All Seasons</option>
            {seasonNames.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        )}
        {/* Source filter */}
        {(() => {
          const sources = [...new Set(leads.map((l) => l.source || "commonwealth_fc"))];
          if (sources.length <= 1) return null;
          return (
            <div className="flex items-center bg-cfc-cream-alt rounded-sm overflow-hidden border border-cfc-border" data-testid="source-filter">
              {[{ id: "all", label: "All Sources" }, ...sources.map((s) => ({ id: s, label: SOURCE_LABELS[s] || s }))].map((f) => (
                <button key={f.id} onClick={() => setSourceFilter(f.id)} className={`px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider ${sourceFilter === f.id ? "bg-cfc-primary text-white" : "text-cfc-text-muted hover:text-cfc-primary"}`} data-testid={`source-filter-${f.id}`}>{f.label}</button>
              ))}
            </div>
          );
        })()}
        <span className="text-xs text-cfc-text-muted ml-auto">{filteredLeads.length} lead{filteredLeads.length !== 1 ? "s" : ""}</span>
      </div>

      {/* Bulk delete bar */}
      {selectedLeads.size > 0 && (
        <div className="flex items-center gap-4 mb-4 p-3 bg-red-50 border border-red-200 rounded-sm" data-testid="bulk-delete-bar">
          <span className="text-sm font-medium text-red-700">{selectedLeads.size} selected</span>
          <Button size="sm" className="bg-red-500 hover:bg-red-600 text-white" onClick={handleBulkDelete} disabled={bulkDeleting} data-testid="bulk-delete-btn">
            {bulkDeleting ? <Loader2 size={14} className="animate-spin mr-1" /> : <Trash2 size={14} className="mr-1" />} Delete
          </Button>
          <Button size="sm" variant="ghost" className="text-red-600" onClick={() => setSelectedLeads(new Set())} data-testid="clear-selection-btn">Clear</Button>
        </div>
      )}

      {/* TABLE VIEW */}
      {viewMode === "table" && (
        <div className="bg-white border border-cfc-border rounded-sm overflow-x-auto" data-testid="leads-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-cfc-border bg-cfc-cream-alt">
                <th className="w-8 px-3 py-2.5"><input type="checkbox" onChange={(e) => { if (e.target.checked) setSelectedLeads(new Set(sortedLeads.map((l) => l.lead_id))); else setSelectedLeads(new Set()); }} className="accent-cfc-primary" /></th>
                {[
                  { key: "name", label: "Name" },
                  { key: "email", label: "Email" },
                  { key: "phone", label: "Phone" },
                  { key: "status", label: "Status" },
                  { key: "teamsnap_team_name", label: "Team" },
                  { key: "source", label: "Source" },
                  { key: "player_age", label: "Age Group" },
                  { key: "created_at", label: "Created" },
                ].map((col) => (
                  <th key={col.key} className="px-3 py-2.5 text-left text-[11px] font-bold uppercase tracking-wider text-cfc-text-muted cursor-pointer hover:text-cfc-primary select-none" onClick={() => toggleSort(col.key)}>
                    <span className="inline-flex items-center gap-1">{col.label} {renderSortIcon(col.key)}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedLeads.map((lead) => (
                <tr
                  key={lead.lead_id}
                  className={`border-b border-cfc-border/50 hover:bg-cfc-cream-alt/50 cursor-pointer transition-colors ${selectedLeads.has(lead.lead_id) ? "bg-red-50" : ""}`}
                  onClick={() => handleLeadClick(lead)}
                  data-testid={`lead-row-${lead.lead_id}`}
                >
                  <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
                    <input type="checkbox" checked={selectedLeads.has(lead.lead_id)} onChange={(e) => toggleLeadSelect(e, lead.lead_id)} className="accent-red-500" data-testid={`select-lead-${lead.lead_id}`} />
                  </td>
                  <td className="px-3 py-2.5 font-medium text-cfc-primary whitespace-nowrap">
                    <div className="flex items-center gap-1.5">
                      {lead.name}
                      {unreadLeads[lead.lead_id] && (
                        <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[9px] font-bold rounded-full bg-red-500 text-white"><Mail size={8} /> {unreadLeads[lead.lead_id]}</span>
                      )}
                      {lead.lead_type === "coach" && <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-[#0C2E3C] text-[#C8E64E]">Coach</span>}
                    </div>
                  </td>
                  <td className="px-3 py-2.5 text-cfc-text-muted truncate max-w-[180px]">{lead.email}</td>
                  <td className="px-3 py-2.5 text-cfc-text-muted whitespace-nowrap">{lead.phone || "—"}</td>
                  <td className="px-3 py-2.5"><StatusBadge statusId={lead.status} /></td>
                  <td className="px-3 py-2.5 whitespace-nowrap">
                    {lead.teamsnap_team_name ? (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-bold rounded bg-indigo-100 text-indigo-700 border border-indigo-200">
                        {lead.teamsnap_team_name}
                      </span>
                    ) : "—"}
                  </td>
                  <td className="px-3 py-2.5"><SourceBadge source={lead.source} /></td>
                  <td className="px-3 py-2.5 text-cfc-text-muted">{lead.player_age || "—"}</td>
                  <td className="px-3 py-2.5 text-cfc-text-muted whitespace-nowrap text-xs">{formatDate(lead.created_at)}</td>
                </tr>
              ))}
              {sortedLeads.length === 0 && (
                <tr><td colSpan={9} className="px-3 py-12 text-center text-cfc-text-muted">No leads match your filters</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* KANBAN VIEW */}
      {viewMode === "kanban" && (
        <div className="flex gap-3 overflow-x-auto pb-4" data-testid="kanban-view">
          {LEAD_STATUSES.map((status) => (
            <div
              key={status.id}
              className="flex-shrink-0 w-64 bg-cfc-cream-alt rounded-sm p-4"
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, status.id)}
              data-testid={`pipeline-column-${status.id}`}
            >
              <div className="flex items-center gap-2 mb-4">
                <div className={`w-3 h-3 rounded-full ${status.color}`} />
                <h3 className="font-heading font-bold text-cfc-primary text-sm">{status.label}</h3>
                <span className="ml-auto bg-cfc-primary text-white text-xs px-2 py-0.5 rounded-full">{leadsForStatus(status.id).length}</span>
              </div>
              <div className="space-y-2 max-h-[60vh] overflow-y-auto">
                {leadsForStatus(status.id).map((lead) => (
                  <Card
                    key={lead.lead_id}
                    className={`cursor-grab border-cfc-border hover:border-cfc-accent active:cursor-grabbing ${selectedLeads.has(lead.lead_id) ? "ring-2 ring-red-300 border-red-300" : ""}`}
                    onClick={() => handleLeadClick(lead)}
                    draggable={selectedLeads.size === 0}
                    onDragStart={(e) => handleDragStart(e, lead)}
                    onDragEnd={handleDragEnd}
                    data-testid={`lead-card-${lead.lead_id}`}
                  >
                    <CardContent className="p-3">
                      <div className="flex items-start gap-2">
                        <input type="checkbox" checked={selectedLeads.has(lead.lead_id)} onChange={(e) => toggleLeadSelect(e, lead.lead_id)} onClick={(e) => e.stopPropagation()} className="mt-0.5 flex-shrink-0 accent-red-500" data-testid={`select-lead-${lead.lead_id}`} />
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-sm text-cfc-primary mb-0.5 flex items-center gap-1.5 flex-wrap">
                            {lead.name}
                            {unreadLeads[lead.lead_id] && <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[9px] font-bold rounded-full bg-red-500 text-white"><Mail size={8} /> {unreadLeads[lead.lead_id]}</span>}
                            {lead.lead_type === "coach" && <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-[#0C2E3C] text-[#C8E64E]">Coach</span>}
                            <SourceBadge source={lead.source} />
                          </h4>
                          <p className="text-[11px] text-cfc-text-muted">{lead.player_age}</p>
                          {lead.teamsnap_team_name && (
                            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[9px] font-bold rounded bg-indigo-100 text-indigo-700 mt-0.5">{lead.teamsnap_team_name}</span>
                          )}
                          <div className="flex items-center gap-1.5 text-[11px] text-cfc-text-muted mt-1">
                            <Mail size={10} /> <span className="truncate">{lead.email}</span>
                          </div>
                          {lead.follow_ups?.some((fu) => !fu.completed) && (
                            <div className="flex items-center gap-1 mt-1.5 text-[11px] text-cfc-accent"><AlertCircle size={10} /> Pending follow-up</div>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <LeadDetailSheet lead={selectedLead} isOpen={sheetOpen} onClose={handleSheetClose} onUpdate={handleUpdate} onDelete={handleLeadDeleted} />
    </div>
  );
};

export default CRMPipeline;
